import { Injectable } from '@angular/core';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

/**
 * Interface representing the current application context
 * This is automatically populated by route navigation and component updates
 */
export interface AppContext {
    screen_name: string;        // Current route/screen identifier
    active_tab: string;          // Currently active tab component
    patient_id: number | null;   // Patient ID if applicable
    program_id: number | null;   // Program ID if applicable
    route_params: any;           // All route parameters
    route_url: string;           // Current full URL
    timestamp: Date;             // When context was last updated
}

/**
 * Central service to track and manage application context
 * Automatically detects route changes and provides reactive context updates
 * Used by AI chat service to provide context-aware assistance
 */
@Injectable({
    providedIn: 'root'
})
export class AppContextService {
    private contextSubject = new BehaviorSubject<AppContext>(this.getInitialContext());
    public context$: Observable<AppContext> = this.contextSubject.asObservable();

    constructor(
        private router: Router,
        private activatedRoute: ActivatedRoute
    ) {
        this.initializeRouterListener();
    }

    /**
     * Initialize listener for router navigation events
     * Automatically updates context when user navigates to different routes
     */
    private initializeRouterListener(): void {
        this.router.events.pipe(
            filter(event => event instanceof NavigationEnd)
        ).subscribe(() => {
            this.updateContextFromRoute();
        });
    }

    /**
     * Get initial context state
     */
    private getInitialContext(): AppContext {
        return {
            screen_name: '',
            active_tab: '',
            patient_id: null,
            program_id: null,
            route_params: {},
            route_url: '',
            timestamp: new Date()
        };
    }

    /**
     * Update context from current route information
     * Extracts route params and generates screen name from URL
     * Preserves entity IDs unless explicitly changed by route params
     */
    private updateContextFromRoute(): void {
        const currentContext = this.contextSubject.value;
        const url = this.router.url;

        // Extract route parameters
        let route = this.activatedRoute;
        while (route.firstChild) {
            route = route.firstChild;
        }

        const params = route.snapshot.params;
        const queryParams = route.snapshot.queryParams;
        const allParams = { ...params, ...queryParams };

        // Generate screen name from URL
        const screenName = this.extractScreenNameFromUrl(url);

        // Extract patient_id and program_id from params if available
        // IMPORTANT: Preserve existing IDs if not present in route params
        // This prevents losing context when navigating to generic routes
        const patientId = allParams['patient_id'] || allParams['id'];
        const programId = allParams['program_id'] || allParams['programme_id'];

        this.updateContext({
            screen_name: screenName,
            active_tab: '', // Reset on route change – components will re-set this via updateTabContext()
            patient_id: patientId ? parseInt(patientId, 10) : currentContext.patient_id, // Preserve if not in route
            program_id: programId ? parseInt(programId, 10) : currentContext.program_id, // Preserve if not in route
            route_params: allParams,
            route_url: url,
            timestamp: new Date()
        });
    }

    /**
     * Extract a meaningful screen name from the URL path
     * Examples: 
     *   /enterprise/program/programs -> program-programs
     *   /program/enrolled-patient -> enrolled-patient
     */
    private extractScreenNameFromUrl(url: string): string {
        // Remove query parameters
        const path = url.split('?')[0];

        // Remove leading/trailing slashes and split
        const segments = path.replace(/^\/|\/$/g, '').split('/');

        // Get last meaningful segment or combine last two
        if (segments.length >= 2) {
            return segments.slice(-2).join('-');
        } else if (segments.length === 1) {
            return segments[0];
        }

        return 'home';
    }

    /**
     * Update the active tab context
     * Called by tab components when they become active
     * @param tabName - Name of the active tab component
     */
    public updateTabContext(tabName: string): void {
        const currentContext = this.contextSubject.value;
        this.updateContext({
            ...currentContext,
            active_tab: tabName,
            timestamp: new Date()
        });
    }

    /**
     * Manually update an entity ID (patient_id, program_id, etc.)
     * @param entityType - Type of entity ('patient_id', 'program_id')
     * @param id - ID value
     */
    public updateEntityId(entityType: 'patient_id' | 'program_id', id: number | null): void {
        const currentContext = this.contextSubject.value;
        this.updateContext({
            ...currentContext,
            [entityType]: id,
            timestamp: new Date()
        });
    }

    /**
     * Update the entire context
     * @param context - New context object
     */
    private updateContext(context: AppContext): void {
        this.contextSubject.next(context);
        console.log('AppContext updated:', context);
    }

    /**
     * Get current context snapshot (non-reactive)
     * @returns Current AppContext
     */
    public getContext(): AppContext {
        return this.contextSubject.value;
    }

    /**
     * Reset context to initial state
     */
    public resetContext(): void {
        this.contextSubject.next(this.getInitialContext());
    }
}
